import React from 'react';
import { Box, IconButton, Tooltip, Typography, Button } from '@mui/material';
import {
  Add,
  Delete,
  Remove,
  Accessible,
  BuildCircle,
  SelfImprovement,
  Person,
} from '@mui/icons-material';
import { colors } from '../../theme/theme';
import {
  LAYOUT_LIMITS,
  RoomOrientation,
  SeatRow,
  SeatPosition,
  SlotState,
} from '../../types/seatLayout';

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  SEAT MAP CANVAS — the room, drawn
 * ─────────────────────────────────────────────────────────────────────────────
 * Rows are CENTRE-ALIGNED to each other, which is what produces the theatre
 * curve when rows have different lengths. Nothing about position is stored — it
 * falls out of the row lengths.
 *
 * ── ORIENTATION ─────────────────────────────────────────────────────────────
 * Row A is always the row NEAREST THE INSTRUCTOR. Orientation decides which wall
 * that is, and everything else follows from two flex axes:
 *
 *   instructor_top     rows stack downward,  seats run left→right   (A at top)
 *   instructor_bottom  rows stack upward,    seats run left→right   (A at bottom)
 *   instructor_left    rows run left→right,  seats stack downward   (A at left)
 *   instructor_right   rows run right→left,  seats stack downward   (A at right)
 *
 * This is done with flex-direction rather than a CSS rotation on purpose: a
 * rotated canvas would turn every bike number on its side and make the row
 * controls unusable. Swapping axes keeps all text upright.
 *
 * Pure presentation: owns no state, mutates nothing. Every interaction is a
 * callback, so the same component renders the editor and a read-only preview.
 */

const SLOT = 34; // px — slot box
const GAP = 6; // px — space between seats
const STRIP = 10; // px — the hover strip that inserts a walkway

/**
 * Who is on a machine, layered over the room's own state. Kept SEPARATE from
 * `SlotState` because they answer different questions — the room says whether a
 * machine exists and works, occupancy says whether it is spoken for tonight. A
 * broken bike and a booked bike must not look the same to staff.
 */
export type SeatOccupancy = 'booked' | 'blocked';

export interface SeatMapCanvasProps {
  rows: SeatRow[];
  /** seatId -> occupancy. Absent = free. Only used by the live session view. */
  occupancy?: Record<string, SeatOccupancy>;
  /**
   * seatId -> rider name, for the tooltip. Supplied ONLY by the staff view —
   * the member-facing endpoint never returns identities, so this stays empty
   * anywhere a member could see it.
   */
  occupantNames?: Record<string, string>;
  frontLabel: string;
  backLabel: string;
  orientation?: RoomOrientation;
  /** Read-only hides every control and disables interaction. */
  readOnly?: boolean;
  /**
   * PICK MODE. A third mode alongside edit and read-only: the room is not being
   * changed and it is not inert either — exactly one free, bookable machine can
   * be chosen.
   *
   * Deliberately separate from `readOnly`/`onCycleSlot`. The editor's click
   * CYCLES a slot's state, which is a destructive act on the room itself; the
   * picker's click SELECTS. Sharing one handler would put a room edit one
   * mis-wired prop away from a front-desk screen.
   *
   * A machine that is booked, blocked, out of service or a walkway is never
   * selectable here — the same rule the server enforces in `resolveSeatClaim`,
   * so the map cannot offer something the write will refuse.
   */
  selectable?: boolean;
  /** The chosen machine's seatId, or null. Only meaningful with `selectable`. */
  selectedSeatId?: string | null;
  onSelectSeat?: (seat: { seatId: string; number: string | null; row: string }) => void;
  /** Click a slot: bookable → out of service → gap → … */
  onCycleSlot?: (rowId: string, seatId: string) => void;
  /** Insert a walkway at `index` within the row (0 = before the first slot). */
  onInsertGap?: (rowId: string, index: number) => void;
  /** Open the per-slot detail popover (number, accessibility, OOS reason). */
  onOpenSlot?: (rowId: string, slot: SeatPosition, anchor: HTMLElement) => void;
  /** Open the per-row popover (row label, surcharge). */
  onOpenRow?: (row: SeatRow, anchor: HTMLElement) => void;
  onRowCountChange?: (rowId: string, delta: 1 | -1) => void;
  onDeleteRow?: (rowId: string) => void;
  onAddRow?: () => void;
}

/**
 * Visual treatment. Kept in one place so the legend cannot drift from the map.
 *
 * The ordering matters: OCCUPANCY WINS over the room's own state, because
 * "someone is on this bike" is the more urgent fact when both are true.
 *
 * ⚠️ SURCHARGE IS NOT RENDERED. A row surcharge is stored but nothing charges
 * it — there is no partial-payment booking path, so a "+₹100 row" ring told
 * staff about a price no member is ever asked for. Nothing that is not wired
 * end to end gets a visual. The `surcharged` argument is kept in the signature
 * (and ignored) so the ring can be restored in one place the day pricing lands.
 */
export const slotStyles = (
  state: SlotState,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  surcharged: boolean,
  occupancy?: SeatOccupancy,
  /** Pick mode: this is the machine the operator has chosen. */
  selected?: boolean,
): React.CSSProperties => {
  // Surcharge accent, dormant until row pricing is actually charged:
  // const surchargeRing = surcharged
  //   ? { boxShadow: `inset 0 0 0 2px ${colors.primary}` }
  //   : {};
  const surchargeRing = {};

  // Wins over everything: the operator's own choice must be unmistakable, and
  // a chosen machine is by definition free, so nothing below can also apply.
  if (selected) {
    return {
      background: colors.success,
      border: `2px solid ${colors.success}`,
      color: colors.white,
      fontWeight: 700,
    };
  }
  if (occupancy === 'booked') {
    return {
      background: colors.primaryDark,
      border: `1px solid ${colors.primaryDark}`,
      color: colors.white,
      fontWeight: 700,
    };
  }
  if (occupancy === 'blocked') {
    return {
      background: colors.greyText3,
      border: `1px solid ${colors.greyText2}`,
      color: colors.white,
    };
  }
  if (state === 'gap') {
    return {
      background: 'transparent',
      border: `1px dashed ${colors.greyText3}`,
      color: 'transparent',
      opacity: 0.5,
    };
  }
  if (state === 'oos') {
    return {
      background: colors.white,
      border: `1.5px dashed ${colors.warning}`,
      color: colors.warning,
      ...surchargeRing,
    };
  }
  return {
    background: colors.white,
    border: `1px solid ${colors.divider}`,
    color: colors.textPrimary,
    ...surchargeRing,
  };
};

const stateTooltip = (
  slot: SeatPosition,
  occ?: SeatOccupancy,
  who?: string,
  selectable?: boolean,
): string => {
  if (occ === 'booked') return `Bike ${slot.label} · ${who ?? 'booked'}`;
  if (occ === 'blocked') return `Bike ${slot.label} · unavailable for this class`;
  if (slot.state === 'gap') {
    return selectable ? 'Walkway' : 'Walkway — click to make it a bike';
  }
  if (slot.state === 'oos') {
    return slot.oosReason
      ? `Bike ${slot.label} · out of service (${slot.oosReason})`
      : `Bike ${slot.label} · out of service`;
  }
  const base = `Bike ${slot.label}${slot.isAccessible ? ' · accessible' : ''}`;
  return selectable ? `${base} · click to choose` : base;
};

const SeatMapCanvas: React.FC<SeatMapCanvasProps> = ({
  rows,
  occupancy,
  occupantNames,
  frontLabel,
  backLabel,
  orientation = 'instructor_top',
  readOnly = false,
  selectable = false,
  selectedSeatId = null,
  onSelectSeat,
  onCycleSlot,
  onInsertGap,
  onOpenSlot,
  onOpenRow,
  onRowCountChange,
  onDeleteRow,
  onAddRow,
}) => {
  // EDIT affordances only. Pick mode is not "interactive" in this sense: it must
  // show no row controls, no insert strips and no add-row button, or a front
  // desk choosing a bike is one stray click from reshaping the room.
  const interactive = !readOnly && !selectable;

  /**
   * `vertical` = the instructor is on a side wall, so rows become columns.
   * `reversed` = the instructor is on the far wall (bottom/right), so row A —
   * which is always nearest the instructor — has to render last along the axis.
   * Flex `-reverse` does that without touching the row array itself, keeping
   * array order (A, B, C…) as the single source of truth.
   */
  const vertical =
    orientation === 'instructor_left' || orientation === 'instructor_right';
  const reversed =
    orientation === 'instructor_bottom' || orientation === 'instructor_right';

  const mainAxis: 'row' | 'column' | 'row-reverse' | 'column-reverse' = vertical
    ? reversed
      ? 'row-reverse'
      : 'row'
    : reversed
      ? 'column-reverse'
      : 'column';

  /** Within a row, seats run along the OTHER axis. */
  const crossAxis: 'row' | 'column' = vertical ? 'column' : 'row';

  const FrontAnchor = (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={vertical ? { px: 1.5 } : { py: 0 }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          px: 2.5,
          py: 1,
          borderRadius: 99,
          background: colors.black,
          color: colors.white,
          whiteSpace: 'nowrap',
        }}
      >
        <SelfImprovement sx={{ fontSize: 16 }} />
        <Typography
          variant="caption"
          sx={{ letterSpacing: 1.2, fontWeight: 700 }}
        >
          {frontLabel || 'INSTRUCTOR'}
        </Typography>
      </Box>
    </Box>
  );

  const BackAnchor = (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={
        vertical
          ? {
              px: 2,
              // Divider runs along the wall the entrance is on.
              borderLeft: reversed ? 'none' : `1px dashed ${colors.greyText3}`,
              borderRight: reversed ? `1px dashed ${colors.greyText3}` : 'none',
              writingMode: 'vertical-rl',
              transform: reversed ? 'rotate(180deg)' : 'none',
            }
          : {
              pt: 2,
              mt: reversed ? 0 : 3,
              mb: reversed ? 3 : 0,
              borderTop: reversed ? 'none' : `1px dashed ${colors.greyText3}`,
              borderBottom: reversed ? `1px dashed ${colors.greyText3}` : 'none',
              pb: reversed ? 2 : 0,
            }
      }
    >
      <Typography
        variant="caption"
        sx={{ letterSpacing: 1.5, color: colors.greyText2, fontWeight: 600 }}
      >
        {backLabel || 'ENTRANCE'}
      </Typography>
    </Box>
  );

  const Seating = (
    <Box
      flex={1}
      display="flex"
      flexDirection={mainAxis}
      // Rows centre-align against each other — this is the theatre curve.
      alignItems="center"
      justifyContent="center"
      gap={1}
      sx={{ py: vertical ? 0 : 2, px: vertical ? 2 : 0 }}
    >
      {rows.map((row) => {
        // Dormant with the rest of the surcharge UI — see slotStyles.
        // const surcharged = row.surcharge > 0;
        const surcharged = false;
        const atMax = row.seats.length >= LAYOUT_LIMITS.MAX_SLOTS_PER_ROW;
        const atMin = row.seats.length <= LAYOUT_LIMITS.MIN_SLOTS_PER_ROW;

        return (
          <Box
            key={row.rowId}
            display="flex"
            // A row lays out along the cross axis: label, seats, controls.
            flexDirection={crossAxis}
            alignItems="center"
            gap={1}
            sx={{ flexShrink: 0 }}
          >
            {/* Row label — click to edit label/surcharge */}
            <Box
              sx={{
                width: 34,
                flexShrink: 0,
                textAlign: 'center',
              }}
            >
              <Typography
                component={interactive && onOpenRow ? 'button' : 'span'}
                onClick={
                  interactive && onOpenRow
                    ? (e: React.MouseEvent<HTMLElement>) =>
                        onOpenRow(row, e.currentTarget)
                    : undefined
                }
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: surcharged ? colors.primaryDark : colors.greyText,
                  border: 'none',
                  background: 'none',
                  cursor: interactive && onOpenRow ? 'pointer' : 'default',
                  p: 0.5,
                  borderRadius: 1,
                  '&:hover':
                    interactive && onOpenRow
                      ? { background: colors.divider }
                      : undefined,
                }}
              >
                {row.label}
              </Typography>
            </Box>

            {/* Centre-aligned slot strip */}
            <Box
              display="flex"
              flexDirection={crossAxis}
              justifyContent="center"
              alignItems="center"
            >
              {/* Leading insert strip */}
              {interactive && onInsertGap && !atMax && (
                <InsertStrip
                  vertical={vertical}
                  onClick={() => onInsertGap(row.rowId, 0)}
                  title={`Insert a walkway at the start of row ${row.label}`}
                />
              )}

              {row.seats.map((slot, i) => {
                const occ = occupancy?.[slot.seatId];
                // The server's rule, mirrored: only a bookable machine that is
                // neither held nor blocked nor broken can be chosen. Keeping
                // this identical to `resolveSeatClaim` is what stops the map
                // offering a bike the write is going to refuse.
                const pickable =
                  selectable && slot.state === 'bookable' && !occ;
                const isPicked = selectable && selectedSeatId === slot.seatId;
                return (
                <React.Fragment key={slot.seatId}>
                  <Tooltip
                    title={stateTooltip(
                      slot,
                      occ,
                      occupantNames?.[slot.seatId],
                      pickable,
                    )}
                    arrow
                  >
                    <Box
                      onClick={
                        pickable && onSelectSeat
                          ? () =>
                              onSelectSeat({
                                seatId: slot.seatId,
                                number: slot.label ?? null,
                                row: row.label,
                              })
                          : interactive && onCycleSlot
                            ? () => onCycleSlot(row.rowId, slot.seatId)
                            : undefined
                      }
                      onContextMenu={
                        interactive && onOpenSlot && slot.state !== 'gap'
                          ? (e: React.MouseEvent<HTMLElement>) => {
                              e.preventDefault();
                              onOpenSlot(row.rowId, slot, e.currentTarget);
                            }
                          : undefined
                      }
                      sx={{
                        // `--slot` is only set on the READ-ONLY map, where a
                        // row carries no controls and the box is purely to be
                        // read. The editor keeps a fixed 34px: its slots are
                        // click targets that cycle state and open menus, and
                        // shrinking those to fit a narrow window trades a real
                        // interaction for a cosmetic one.
                        width: 'var(--slot, 34px)',
                        height: 'var(--slot, 34px)',
                        // Spacing follows the axis the seats run along.
                        mx: vertical ? 0 : `${GAP / 2}px`,
                        my: vertical ? `${GAP / 2}px` : 0,
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        position: 'relative',
                        fontSize: 12,
                        fontWeight: 600,
                        userSelect: 'none',
                        transition: 'transform .08s, box-shadow .08s',
                        cursor: interactive || pickable ? 'pointer' : 'default',
                        ...slotStyles(slot.state, surcharged, occ, isPicked),
                        '&:hover':
                          interactive || pickable
                            ? {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 2px 8px ${colors.greyText3}`,
                              }
                            : undefined,
                      }}
                    >
                      {slot.state !== 'gap' && slot.label}

                      {slot.state === 'oos' && (
                        <BuildCircle
                          sx={{
                            position: 'absolute',
                            top: -5,
                            right: -5,
                            fontSize: 13,
                            color: colors.warning,
                            background: colors.white,
                            borderRadius: '50%',
                          }}
                        />
                      )}
                      {occupancy?.[slot.seatId] === 'booked' && (
                        <Person
                          sx={{
                            position: 'absolute',
                            top: -6,
                            right: -6,
                            fontSize: 13,
                            color: colors.primaryDark,
                            background: colors.white,
                            borderRadius: '50%',
                          }}
                        />
                      )}
                      {slot.isAccessible && slot.state !== 'gap' && (
                        <Accessible
                          sx={{
                            position: 'absolute',
                            bottom: -5,
                            left: -5,
                            fontSize: 13,
                            color: colors.info,
                            background: colors.white,
                            borderRadius: '50%',
                          }}
                        />
                      )}
                    </Box>
                  </Tooltip>

                  {/* Trailing insert strip after each slot */}
                  {interactive && onInsertGap && !atMax && (
                    <InsertStrip
                      vertical={vertical}
                      onClick={() => onInsertGap(row.rowId, i + 1)}
                      title={`Insert a walkway after position ${i + 1} in row ${row.label}`}
                    />
                  )}
                </React.Fragment>
                );
              })}
            </Box>

            {/* Row length + delete */}
            {interactive && (
              <Box
                display="flex"
                // Controls stack the same way the row does, so they stay
                // readable when the room is drawn as columns.
                flexDirection={crossAxis}
                alignItems="center"
                gap={0.25}
                sx={{
                  flexShrink: 0,
                  border: `1px solid ${colors.divider}`,
                  borderRadius: 99,
                  px: vertical ? 0.25 : 0.5,
                  py: vertical ? 0.5 : 0.25,
                  background: colors.white,
                }}
              >
                <Tooltip title="Remove the last slot">
                  <span>
                    <IconButton
                      size="small"
                      disabled={atMin}
                      onClick={() => onRowCountChange?.(row.rowId, -1)}
                    >
                      <Remove sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Typography
                  variant="caption"
                  sx={{ minWidth: 18, textAlign: 'center', fontWeight: 600 }}
                >
                  {row.seats.length}
                </Typography>
                <Tooltip
                  title={
                    atMax
                      ? `Max ${LAYOUT_LIMITS.MAX_SLOTS_PER_ROW} seats per row`
                      : 'Add a slot at the end'
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={atMax}
                      onClick={() => onRowCountChange?.(row.rowId, 1)}
                    >
                      <Add sx={{ fontSize: 16 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={`Delete row ${row.label}`}>
                  <IconButton
                    size="small"
                    onClick={() => onDeleteRow?.(row.rowId)}
                  >
                    <Delete sx={{ fontSize: 15, color: colors.greyText2 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        );
      })}

      {/* Add row — sits at the far end of the row axis, i.e. furthest from the
          instructor, which is where the next row would physically go. */}
      {interactive && onAddRow && (
        <Button
          onClick={onAddRow}
          startIcon={<Add />}
          size="small"
          disabled={rows.length >= LAYOUT_LIMITS.MAX_ROWS}
          sx={{
            borderRadius: 99,
            textTransform: 'none',
            border: `1px dashed ${colors.greyText3}`,
            color: colors.greyText,
            px: 2,
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {rows.length >= LAYOUT_LIMITS.MAX_ROWS
            ? `Max ${LAYOUT_LIMITS.MAX_ROWS} rows`
            : 'Add a row'}
        </Button>
      )}
    </Box>
  );

  // Seats in the longest row. Every row is scaled against this one number so
  // rows stay aligned column to column — scaling each row to its own length
  // would make a longer row's seats smaller and skew the room's geometry.
  const widestRow = rows.reduce((m, r) => Math.max(m, r.seats.length), 1);
  // Label gutter + the row's own padding, taken off before dividing.
  const rowChrome = 34 + 24;

  return (
    <Box
      sx={{
        borderRadius: 3,
        p: { xs: 2, sm: 3 },
        background: `linear-gradient(180deg, ${colors.white} 0%, ${colors.backgroundTertiary} 100%)`,
        border: `1px solid ${colors.divider}`,
        overflowX: 'auto',
        ...(readOnly && !vertical
          ? {
              ['--slot' as string]: `max(22px, min(34px, calc((100% - ${rowChrome + GAP * widestRow}px) / ${widestRow})))`,
            }
          : {}),
      }}
    >
      {/*
        Order is always [front, seating, back]. Flex `-reverse` on the main axis
        flips it for the far-wall orientations, so this JSX never branches on
        which end things go — the axis does the work.
      */}
      <Box
        display="flex"
        flexDirection={mainAxis}
        alignItems="stretch"
        sx={{ minHeight: vertical ? 220 : undefined, minWidth: 'fit-content' }}
      >
        {FrontAnchor}
        {Seating}
        {BackAnchor}
      </Box>
    </Box>
  );
};

/**
 * The thin strip between two seats. Invisible until hovered, then shows as a
 * primary-coloured sliver — the affordance for "push a walkway in here".
 * Swaps its long axis so it still reads as a divider when rows are columns.
 */
const InsertStrip: React.FC<{
  onClick: () => void;
  title: string;
  vertical: boolean;
}> = ({ onClick, title, vertical }) => (
  <Tooltip title={title} arrow>
    <Box
      onClick={onClick}
      sx={{
        width: vertical ? SLOT : STRIP,
        height: vertical ? STRIP : SLOT,
        flexShrink: 0,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover > div': { opacity: 1, transform: 'scale(1)' },
      }}
    >
      <Box
        sx={{
          width: vertical ? '70%' : 3,
          height: vertical ? 3 : '70%',
          borderRadius: 99,
          background: colors.primary,
          opacity: 0,
          transform: vertical ? 'scaleX(.4)' : 'scaleY(.4)',
          transition: 'opacity .1s, transform .1s',
        }}
      />
    </Box>
  </Tooltip>
);

export default SeatMapCanvas;
